import React, { ReactNode, useEffect, useState } from 'react'
import { useKeycloak } from 'react-keycloak'
import { Skeleton } from '@material-ui/lab'
import { get as _get } from 'lodash'
import { Layer, Stage } from 'react-konva'
import { FbSelect, GridContainer, GridItem, Icons } from '@fabricks/ui-components'
import { RackPosition } from '../../components/Shelf/shelf'
import {
    GenericDto,
    StorageEntryDto,
    useRacksLazyQuery,
    useStorageEntriesLazyQuery,
    useZonesLazyQuery
} from '../../api/models'
import { render } from "react-dom";

const renderShelfData = (
    entries: Map<String, StorageEntryDto[]>,
    config: {
        padding?: number; width?: number; height?: number,
        colorForBox?: (box: StorageEntryDto) => string,
        subText?: (box: StorageEntryDto) => string
    },
) => {
    let levelIndex = 0
    const rackPositionNodes: ReactNode[] = []

    entries.forEach(value => {
        const amountOfBoxesOnLevel = value.length
        const padding = config.padding || 8
        const shelfWidth = config.width || 1100
        const shelfHeight = config.height || 500
        const width = (shelfWidth - amountOfBoxesOnLevel * padding - padding) / amountOfBoxesOnLevel
        const height = (shelfHeight - entries.size * padding - padding) / entries.size

        const y = levelIndex * (height + padding) + padding

        const calculateColor = (box: StorageEntryDto) => {
            if (!box.material) {
                return '#467abd'
            }

            if (box.currentStock == null || box.currentStock <= 0) {
                return 'orange'
            }

            if (_get(box, 'fuelLevel', 0) as number > 1 || box.isOverflow) {
                return 'rgb(254, 85, 112)'
            }

            return `rgba(0,0,0, ${(_get(box, 'fuelLevel', 0) as number) + 0.1})`
        }

        const renderedPos = value.map((box, index: number) => {
            return (
                <RackPosition
                    height={height}
                    width={width}
                    mainText={`${box.zone}${box.rack}${box.level}${box.position}`}
                    boxColor={config.colorForBox ? config.colorForBox(box) : calculateColor(box)}
                    x={index * (width + padding) + padding}
                    y={y}
                    textColor="black"
                    subText={config.subText ? config.subText(box) : _get(box, 'material.number', '---')}
                />
            )
        })
        levelIndex += 1
        rackPositionNodes.push(renderedPos)
    })
    return rackPositionNodes
}

const groupEntriesByLevel = (entries: StorageEntryDto[]): Map<String, StorageEntryDto[]> => {
    return entries
        .sort((a, b) => (((a.level || '') as any) > (b.level || '') ? -1 : 1) as any)
        .filter((entry) => entry.boxType)
        .reduce((acc: Map<any, StorageEntryDto[]>, current: StorageEntryDto) => {
            return acc.has(current.level)
                ? acc.set(current.level, [...(acc.get(current.level) || []), current])
                : acc.set(current.level, [current])
        }, new Map())
}

interface RackViewProps {
    entries: StorageEntryDto[]
    padding?: number
    width?: number
    legendEntries?: {title: string; color: string; backgroundColor: string}[]
    colorForBox?: (box: StorageEntryDto) => string
    subText?: (box: StorageEntryDto) => string
}

export const RackView: React.FC<RackViewProps> = (props: RackViewProps) => {
    const { padding, width, entries, legendEntries, colorForBox, subText } = props

    if (entries.length === 0) {
        return null
    }

    const groupedEntries = groupEntriesByLevel(entries)
    const usedWidth = width || 1100
    const usedHeight = groupedEntries.size * 50

    const renderLegend = () => {
        // eslint-disable-next-line react/prop-types
        if (!legendEntries || legendEntries.length === 0) {
            return null
        }

        return (
            <RackViewLegend usedWidth={usedWidth} entries={legendEntries} />
        )
    }

    return (
        <div>
            {renderLegend()}
            <div style={{ overflow: 'hidden', overflowX: 'scroll', display: 'flex', justifyContent: 'center' }}>
                <Stage width={usedWidth} height={usedHeight}>
                    <Layer>
                        {renderShelfData(groupedEntries, {
                            padding,
                            height: usedHeight,
                            width: usedWidth,
                            colorForBox,
                            subText
                        })}
                    </Layer>
                </Stage>
            </div>
        </div>
    )
}


interface LegendEntry {
    title: string,
    color: string,
    backgroundColor: string
}

interface RackViewLegendProps {
    entries: LegendEntry[]
    usedWidth: number
}

export const RackViewLegend: React.FC<RackViewLegendProps> = (props: RackViewLegendProps) => {
    const { entries, usedWidth } = props

    if (entries.length === 0) {
        return null
    }

    const legendEntries = entries.map((entryElement: any) =>
        <li style={{
            display: "inline",
            padding: "5px",
            margin: "10px",
            backgroundColor: entryElement.backgroundColor
        }}><span style={{ color: entryElement.color }}>{entryElement.title}</span></li>
    );

    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ul style={{
                width: usedWidth,
                border: "1px dashed #ffffff",
                display: "flex",
                justifyContent: "center",
                listStyle: "none"
            }}>
                {legendEntries}
            </ul>
        </div>
    )
}

export const RackViewControl: React.FC<any> = () => {
    const [keycloak] = useKeycloak()
    const [zone, setZone] = useState('')
    const [rack, setRack] = useState('')
    const [viewType, setViewType] = useState('fr')

    const [getEntries, entries] = useStorageEntriesLazyQuery()
    const [getRacks, racks] = useRacksLazyQuery()
    const [getZones, zones] = useZonesLazyQuery()

    const rackOptions = _get(racks, 'data.getRacksPerZone.data', []).map((rackDto: GenericDto) => ({
        label: rackDto.value,
        value: rackDto.uuid,
    }))

    const zoneOptions = _get(zones, 'data.getZones.data', []).map((zoneDto: GenericDto) => ({
        label: zoneDto.value,
        value: zoneDto.uuid,
    }))

    const viewTypeOptions = [{
        label: "Efficiency",
        value: "ef",
    }, {
        label: "Fill ratio",
        value: "fr"
    }]

    /*useEffect(() => {
        if (viewType) {
            alert("Change viewtype of rackview to " + viewType)
        }
    }, [viewType])*/

    useEffect(() => {
        getZones({
            context: { headers: { Authorization: `Bearer ${keycloak.token || 'unknown'}` } },
        })
    }, [getZones, keycloak])

    useEffect(() => {
        if (!zone) {
            return
        }
        getRacks({
            context: { headers: { Authorization: `Bearer ${keycloak.token || 'unknown'}` } },
            variables: { zone },
        })
    }, [getRacks, zone, keycloak])

    useEffect(() => {
        if (rack.length === 0 || zone.length === 0) {
            return
        }
        getEntries({
            context: { headers: { Authorization: `Bearer ${keycloak.token || 'unknown'}` } },
            variables: {
                filter: [{ field: 'zone', expected: zone }, { field: 'rack', expected: rack }],
                pagination: { size: Math.pow(2, 16) - 1 },
            },
        })
    }, [getEntries, zone, rack, keycloak])

    if (entries.loading) {
        return (
            <>
                <Skeleton variant="rect" animation="wave" width="100%" height={100} style={{ marginBottom: '5px' }} />
            </>
        )
    }

    if (entries.error) {
        return (
            <div style={{ textAlign: 'center', color: 'red' }}>
                <Icons.Error color="error" style={{ fontSize: '45px' }} />
                <div>An unknown error has occurred</div>
            </div>
        )
    }

    const colorForBox = () => {
        if (viewType === 'fr') {
            const calculateColorForFuelLevel = (box: StorageEntryDto) => {
                if (!box.material) {
                    return '#467abd'
                }

                if ((_get(box, 'currentStock', 0) as number) <= 0) {
                    return '#ff8800'
                }

                if (_get(box, 'fuelLevel', 0) as number > 1 || box.isOverflow) {
                    return 'rgb(254, 85, 112)'
                }
                return `rgba(105, 255, 132, ${(_get(box, 'fuelLevel', 0) as number) + 0.1})`
            }
            return calculateColorForFuelLevel
        }
        if (viewType === 'ef') {
            const maxRacks = rackOptions ? rackOptions.length : 0
            const customC = maxRacks * 350

            const calculateColorForEff = (box: StorageEntryDto) => {
                const saturationFactor = 1 - (_get(box, 'effNumber', 0) as number) / customC + 0.3
                const redRangeMin = 11
                const redWeight = 0.3

                const greenRangeMin = 0.8
                const greenWeight = 1

                const blueRangeMin = 2
                const blueWeight = 0.4

                const red = Math.round(redRangeMin + (saturationFactor * redWeight * (255 - redRangeMin)))
                const green = Math.round(greenRangeMin + (saturationFactor * greenWeight * (255 - greenRangeMin)))
                const blue = Math.round(blueRangeMin + (saturationFactor * blueWeight * (255 - blueRangeMin)))

                return `rgba(${red}, ${green}, ${blue}, ${saturationFactor})`
            }
            return calculateColorForEff
        }
        return undefined
    }

    const getLegendEntries = () => {
        if (viewType === 'ef') {
            return []
        }

        return [
            { title: "Empty Box", color: '#FFF', backgroundColor: '#467abd' },
            { title: "Overflow", color: '#FFF', backgroundColor: 'rgb(254, 85, 112)' },
            { title: "Fill ratio", color: '#000', backgroundColor: 'rgb(105, 327, 132)' },
            { title: "Material with no stock", color: '#000', backgroundColor: '#ff8800' },
        ]
    }

    return (
        <>
            <FbSelect
                id="zone-filter"
                value={zone}
                label="Zone"
                options={zoneOptions}
                onChange={event => setZone(event.target.value as string)}
            />
            <FbSelect
                id="rack-filter"
                value={rack}
                label="Rack"
                options={rackOptions}
                onChange={event => setRack(event.target.value as string)}
            />
            <FbSelect
                id="rack-viewtype"
                value={viewType}
                label="Viewtype"
                options={viewTypeOptions}
                onChange={event => setViewType(event.target.value as string)}
            />
            <RackView
                colorForBox={colorForBox()}
                subText={viewType === 'ef' ? (box: StorageEntryDto) => `${box.effNumber}` : undefined}
                legendEntries={getLegendEntries()}
                entries={_get(entries, 'data.getEntries.data', []) as StorageEntryDto[]} />
        </>
    )
}
